import { useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api.js'
import { candidateLabel } from '../../lib/bowlCoverage.js'
import styles from './repVolume.module.css'
import {createDeskPickController} from '../../lib/bowlDeskPickState.js'
import {useSessionGeneration} from '../../lib/useSessionGeneration.js'

/**
 * WHO IS GOING AFTER IT — the split-conference selector.
 *
 * Brian, 2026-10-26: "The split conferences in bowl opportunity need to be able
 * to have a selector to select who is going after it out of the ones you
 * surface."
 *
 * ⚠️ IT OFFERS ONLY WHAT THE SERVER SURFACED. `candidates` comes from the
 * coverage object on the board payload (the conference's reps plus the reps on
 * named school desks in it). The server re-checks every pick against the same
 * list, refuses Emily and Madison-alone by name, and refuses anyone without
 * Manage Assignments standing, so hiding this control from a rep is a courtesy
 * and not the gate.
 *
 * ⚠️ A REASON IS REQUIRED, and the pick is a NEW ROW on an append-only ledger
 * with the picker's name and the time on it. Changing it later is another pick.
 */
export default function DeskPicker({ school = '', conference = '', candidates = [], current = '', expectedVersion = null, season = null, onPicked, label = '', defaultOpen = false }) {
  const [rep, setRep] = useState('')
  const [reason, setReason] = useState('')
  const [state, setState] = useState({ status: 'idle', msg: '' })
  const generation = useSessionGeneration()
  const controller = useRef(null)
  const picked = useRef(onPicked)
  picked.current = onPicked
  useEffect(() => {
    const next = createDeskPickController({
      send: body => api.fbsBowlDeskPick(body),
      onState: setState,
      onPicked: result => { setReason(''); picked.current?.(result) },
    })
    controller.current = next
    setRep(''); setReason(''); setState({status: 'idle', msg: ''})
    return () => { next.dispose(); if (controller.current === next) controller.current = null }
  }, [school, conference, generation, expectedVersion, season])
  const submit = e => {
    e.preventDefault()
    controller.current?.submit({school, conference, rep, reason, expectedVersion, season}, candidates)
  }
  const blocked = state.status === 'saving' || state.status === 'uncertain'

  if (!Array.isArray(candidates) || candidates.length < 2) return null

  return (
    <details className={styles.pickBox} open={defaultOpen || undefined}>
      <summary className={styles.deskAction}>
        {label || (school ? 'Choose who is going after it' : `Choose one desk for every ${conference} school without its own`)}
      </summary>
      <form className={styles.pickForm} onSubmit={submit}>
        <label className={styles.pickLabel}>
          Rep
          <select disabled={blocked} value={rep} onChange={(e) => setRep(e.target.value)} required>
            <option value="">Pick one of {candidates.length}</option>
            {candidates.map((c) => (
              <option key={c.short} value={c.short} disabled={c.name === current}>
                {candidateLabel(c)}{c.name === current ? ' (now)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.pickLabel}>
          Why
          <input type="text" disabled={blocked} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Chris already has the athletics contact" maxLength={300} required />
        </label>
        <button className="btn" type="submit" disabled={blocked}>
          {state.status === 'saving' ? 'Saving…' : 'Save pick'}
        </button>
        {state.msg && (
          <span className={['error', 'uncertain'].includes(state.status) ? styles.deskBad : styles.deskWhy} role="status">{state.msg}</span>
        )}
      </form>
    </details>
  )
}
